import React from "react";
import { Brain, GitBranch, Play, Zap, CheckCircle2 } from "lucide-react";

/**
 * Visual Flow Builder Showcase
 * Mimics the product's workflow editor: purple agent nodes,
 * orange condition node, teal end nodes, with animated dashed edges.
 */
export default function WorkflowShowcase() {
  return (
    <section
      id="workflows"
      data-testid="workflow-section"
      className="relative py-28 md:py-40 overflow-hidden"
    >
      <div className="pointer-events-none absolute top-0 left-0 w-[600px] h-[600px] glow-teal opacity-30" />
      <div className="pointer-events-none absolute bottom-0 right-0 w-[600px] h-[600px] glow-purple opacity-30" />

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
        <div className="grid lg:grid-cols-[0.9fr_1.1fr] gap-16 items-center">
          <div>
            <div className="inline-flex items-center gap-2 mb-5">
              <span className="text-[11px] font-mono tracking-[0.2em] uppercase text-[#7C5CFF]">
                ── Visual flow builder
              </span>
            </div>
            <h2 className="font-serif text-4xl md:text-6xl leading-[1.02] tracking-tight text-white mb-6">
              Design{" "}
              <span className="italic text-white/90">multi-agent</span>
              <br />
              workflows visually.
            </h2>
            <p className="text-white/60 text-lg leading-relaxed max-w-xl mb-8">
              Compose triggers, classifier agents, conditions and actions on a
              drag-and-drop canvas. Every node is versioned, testable and
              observable — from webhook to resolution.
            </p>

            <ul className="space-y-3">
              {[
                "Webhook, schedule and channel-based triggers",
                "Classifier agent → conditional routing in one pass",
                "Per-node test runs with full payload inspection",
                "Publish draft → v5 → v6 — roll back in one click",
              ].map((t, i) => (
                <li
                  key={i}
                  className="flex items-start gap-3 text-white/75 text-[15px]"
                  data-testid={`workflow-bullet-${i}`}
                >
                  <CheckCircle2 className="w-4 h-4 mt-1 text-[#3DDFB3] flex-shrink-0" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Canvas mock */}
          <div className="relative" data-testid="workflow-canvas">
            <div className="absolute -inset-6 rounded-[28px] bg-gradient-to-br from-[#7C5CFF]/20 via-transparent to-[#3DDFB3]/10 blur-2xl pointer-events-none" />
            <div className="relative rounded-2xl border border-white/10 bg-[#0b0b13] p-4 md:p-6 overflow-hidden">
              {/* toolbar */}
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-2">
                  <span className="pill bg-[#7C5CFF]/15 text-[#b9a9ff] border border-[#7C5CFF]/30">
                    Published
                  </span>
                  <span className="text-[11px] font-mono text-white/40">
                    v68 · charlie
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <button className="btn-ghost px-3 py-1.5 rounded-lg text-xs text-white/70 inline-flex items-center gap-1.5">
                    <Play className="w-3 h-3" /> Test
                  </button>
                </div>
              </div>

              {/* Canvas grid bg */}
              <div className="relative h-[420px] rounded-xl border border-white/5 bg-[radial-gradient(circle_at_1px_1px,rgba(255,255,255,0.06)_1px,transparent_0)] [background-size:18px_18px] overflow-hidden">
                {/* SVG edges */}
                <svg
                  className="absolute inset-0 w-full h-full pointer-events-none"
                  viewBox="0 0 100 100"
                  preserveAspectRatio="none"
                >
                  {/* Trigger -> Classifier */}
                  <path
                    d="M 10 82 C 18 82, 22 50, 30 50"
                    stroke="#7C5CFF"
                    strokeOpacity="0.6"
                    strokeWidth="0.3"
                    fill="none"
                    className="flow-dash"
                    vectorEffect="non-scaling-stroke"
                    style={{ strokeWidth: 1.2 }}
                  />
                  {/* Classifier -> Condition */}
                  <path
                    d="M 43 50 L 55 50"
                    stroke="#7C5CFF"
                    strokeOpacity="0.6"
                    fill="none"
                    className="flow-dash"
                    vectorEffect="non-scaling-stroke"
                    style={{ strokeWidth: 1.2 }}
                  />
                  {/* Condition -> Refund */}
                  <path
                    d="M 70 42 C 76 42, 78 18, 84 18"
                    stroke="#F97316"
                    strokeOpacity="0.7"
                    fill="none"
                    className="flow-dash"
                    vectorEffect="non-scaling-stroke"
                    style={{ strokeWidth: 1.2 }}
                  />
                  {/* Condition -> Info */}
                  <path
                    d="M 70 50 L 84 50"
                    stroke="#F97316"
                    strokeOpacity="0.7"
                    fill="none"
                    className="flow-dash"
                    vectorEffect="non-scaling-stroke"
                    style={{ strokeWidth: 1.2 }}
                  />
                  {/* Condition -> Escalate */}
                  <path
                    d="M 70 58 C 76 58, 78 82, 84 82"
                    stroke="#F97316"
                    strokeOpacity="0.7"
                    fill="none"
                    className="flow-dash"
                    vectorEffect="non-scaling-stroke"
                    style={{ strokeWidth: 1.2 }}
                  />
                </svg>

                {/* Trigger node */}
                <Node
                  x="2%"
                  y="74%"
                  kind="trigger"
                  title="Trigger"
                  subtitle="Webhook"
                  icon={Zap}
                />

                {/* Classifier agent */}
                <Node
                  x="24%"
                  y="38%"
                  kind="agent"
                  title="Classifier"
                  subtitle="gpt-4o-mini"
                  icon={Brain}
                  meta="classification · enum(3)"
                />

                {/* Condition */}
                <Node
                  x="48%"
                  y="38%"
                  kind="condition"
                  title="Condition"
                  subtitle="If / Else"
                  icon={GitBranch}
                  meta={
                    <div className="space-y-1">
                      <div className="text-[10px] text-[#F97316]">
                        if · cancel_subscription
                      </div>
                      <div className="text-[10px] text-[#F97316]/70">
                        elif · get_information
                      </div>
                      <div className="text-[10px] text-[#F97316]/70">
                        elif · return_item
                      </div>
                    </div>
                  }
                />

                {/* Branch nodes */}
                <Node
                  x="74%"
                  y="8%"
                  kind="agent"
                  title="Refund Agent"
                  subtitle="response_schema"
                  icon={Brain}
                />
                <Node
                  x="74%"
                  y="42%"
                  kind="agent"
                  title="Info Agent"
                  subtitle="response_schema"
                  icon={Brain}
                />
                <Node
                  x="74%"
                  y="76%"
                  kind="end"
                  title="Human"
                  subtitle="Escalate"
                />
              </div>

              {/* node palette */}
              <div className="mt-4 flex items-center gap-2 flex-wrap text-[11px] font-mono uppercase tracking-[0.12em] text-white/45">
                <span>Add node:</span>
                <Palette label="Start" color="#3DDFB3" />
                <Palette label="Agent" color="#7C5CFF" icon={Brain} />
                <Palette label="If/Else" color="#F97316" icon={GitBranch} />
                <Palette label="End" color="#3DDFB3" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Node({ x, y, kind, title, subtitle, icon: Icon, meta }) {
  const colorMap = {
    agent: { bg: "#7C5CFF", tint: "rgba(124,92,255,0.18)" },
    condition: { bg: "#F97316", tint: "rgba(249,115,22,0.15)" },
    end: { bg: "#3DDFB3", tint: "rgba(61,223,179,0.15)" },
    trigger: { bg: "#7C5CFF", tint: "rgba(124,92,255,0.18)" },
  };
  const c = colorMap[kind];
  return (
    <div
      className="absolute rounded-lg border backdrop-blur-sm"
      style={{
        left: x,
        top: y,
        background: c.tint,
        borderColor: `${c.bg}60`,
        width: 130,
        boxShadow: `0 0 28px ${c.bg}20`,
      }}
    >
      <div className="px-3 py-2.5">
        <div className="flex items-center gap-2 text-[11px] text-white/90">
          {Icon ? (
            <Icon className="w-3.5 h-3.5" style={{ color: c.bg }} />
          ) : (
            <span
              className="w-2 h-2 rounded-full"
              style={{ background: c.bg }}
            />
          )}
          <span className="font-medium">{title}</span>
        </div>
        <div className="text-[10px] text-white/45 mt-0.5">{subtitle}</div>
        {meta ? (
          <div className="mt-2 pt-2 border-t border-white/5 text-[10px] text-white/60 font-mono">
            {meta}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function Palette({ label, color, icon: Icon }) {
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md border"
      style={{
        background: `${color}14`,
        borderColor: `${color}40`,
        color,
      }}
    >
      {Icon ? <Icon className="w-3 h-3" /> : null}
      {label}
    </span>
  );
}
